import { Box, Tooltip } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { AgingBucket } from 'hooks/analytics';
import { AnalyticsColors } from './analyticsUi';
import { formatCurrency } from 'utils/formatters';

const severityBarColors: Record<string, string> = {
    green: '#059669',
    amber: '#D97706',
    red: '#DC2626',
    darkred: '#991B1B',
};

interface AgingBucketChartProps {
    buckets: AgingBucket[];
    total: number;
    totalCount: number;
    onBucketClick?: (bucketIndex: number) => void;
}

const AgingBucketChart = ({ buckets, total, totalCount, onBucketClick }: AgingBucketChartProps) => {
    const maxValue = Math.max(...buckets.map((b) => b.totalValue), 1);
    const clickable = !!onBucketClick;

    return (
        <Box sx={{ width: '100%' }}>
            {buckets.map((bucket, i) => (
                <Tooltip
                    key={i}
                    title={`${bucket.count} items · ${formatCurrency(bucket.totalValue)}${onBucketClick ? ' — click to see details' : ''}`}
                    arrow
                >
                    <Box
                        onClick={() => onBucketClick?.(i)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            mb: '8px',
                            cursor: clickable ? 'pointer' : 'default',
                            borderRadius: '6px',
                            py: '2px',
                            transition: 'background 0.1s',
                            ...(clickable && {
                                '&:hover': {
                                    background: '#F9FAFB',
                                },
                            }),
                        }}
                    >
                        <Box
                            sx={{
                                width: 72,
                                fontSize: 12,
                                fontWeight: 600,
                                color: AnalyticsColors.textSecondary,
                                textAlign: 'right',
                                flexShrink: 0,
                            }}
                        >
                            {bucket.label}
                        </Box>
                        <Box
                            sx={{
                                flex: 1,
                                height: 24,
                                background: '#F3F4F6',
                                borderRadius: '6px',
                                overflow: 'hidden',
                                position: 'relative',
                            }}
                        >
                            <Box
                                sx={{
                                    height: '100%',
                                    width: `${Math.max((bucket.totalValue / maxValue) * 100, 4)}%`,
                                    background: severityBarColors[bucket.severity] || '#8B5CF6',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    pl: '8px',
                                    transition: 'width 0.4s ease',
                                }}
                            >
                                <Box
                                    component="span"
                                    sx={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        color: 'white',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {bucket.count}
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                width: 80,
                                fontSize: 12,
                                fontWeight: 600,
                                color: AnalyticsColors.textPrimary,
                                textAlign: 'right',
                                flexShrink: 0,
                            }}
                        >
                            {formatCurrency(bucket.totalValue)}
                        </Box>
                        {onBucketClick && (
                            <Box
                                component="span"
                                sx={{
                                    flexShrink: 0,
                                    color: AnalyticsColors.textTertiary,
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <ChevronRight sx={{ fontSize: 14 }} />
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            ))}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pt: '10px',
                    mt: '6px',
                    borderTop: `1px solid ${AnalyticsColors.borderLight}`,
                }}
            >
                <Box
                    component="span"
                    sx={{ fontSize: 13, fontWeight: 600, color: AnalyticsColors.textSecondary }}
                >
                    {totalCount} total
                </Box>
                <Box
                    component="span"
                    sx={{ fontSize: 16, fontWeight: 700, color: AnalyticsColors.textPrimary }}
                >
                    {formatCurrency(total)}
                </Box>
            </Box>
        </Box>
    );
};

export default AgingBucketChart;
